import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  untracked,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { iconoirMail,
  iconoirArrowRight,
  iconoirBook,
  iconoirBuilding,
  iconoirCalendar,
  iconoirCamera,
  iconoirCheck,
  iconoirCity,
  iconoirCode,
  iconoirCommunity,
  iconoirComputer,
  iconoirFolder,
  iconoirGraduationCap,
  iconoirLaptop,
  iconoirLogOut,
  iconoirMenu,
  iconoirMicrophone,
  iconoirNavArrowDown,
  iconoirNavArrowRight,
  iconoirPlus,
  iconoirSettings,
  iconoirSidebarCollapse,
  iconoirSidebarExpand,
  iconoirTransitionRight,
  iconoirViewGrid,
  iconoirXmark,
} from '@ng-icons/iconoir';
import { filter, map, Subscription } from 'rxjs';
import {
  AvatarChangeDialogComponent,
  AvatarChangeDialogData,
} from '../../avatar/avatar-change-dialog/avatar-change-dialog.component';
import { AuthService } from '../../../../core/auth/auth.service';
import { TrialBannerService } from '../../../../services/trial-banner.service';
import { sidebarModePref } from '../../../../services/sidebar-mode.preference';
import { ViewPreferencesService } from '../../../../services/view-preferences.service';
import { SubscriptionFeaturesService } from '../../../../services/subscription-features.service';
import { VoiceOverlayService } from '../../../../core/voice/services/voice-overlay.service';
import { Role } from '../../../../models/enums';
// Exception ciblée : accès à ProjectFavoritesService depuis components/ui, cf. tâche #37
import { ProjectFavoritesService } from '../../../../modules/project-management/services/project-favorites.service';
// Même exception : les dossiers favoris (MOKA-214) s'affichent sous « Projets ».
import { ProjectFoldersService } from '../../../../modules/project-management/services/project-folders.service';
import { QuickTaskProject } from '../../../../modules/project-management/components/quick-task-popover/quick-task-popover.component';
import { SIDENAV } from './sidenav-context';
import { isLocalHost, isMokahAdmin } from '../../../../core/guards/access/developer-hub.guard';
import { SidenavCafeComponent } from './variants/sidenav-cafe/sidenav-cafe.component';

export type SidebarMode = 'expanded' | 'collapsed' | 'closed';

type ModuleKey =
  | 'calendars_enable'
  | 'projects_enable'
  | 'human_resources_enable'
  | 'inventory_enable';

interface ExternalRole {
  role: { identifier: string };
}

export interface UserProject {
  id: string;
  name: string;
  identifier?: string;
}

// Vérification de compatibilité structurelle avec l'input `projects` de
// app-quick-task-popover : un UserProject[] doit pouvoir être passé tel quel.
type _UserProjectIsQuickTaskProject = UserProject extends QuickTaskProject ? true : never;

interface ProjectMembership {
  project: { id: string; name: string } | null;
}

export interface NavItem {
  label: string;
  route: string;
  icon: string;
  color: string;
  badge?: () => number;
  routerLinkExact?: boolean;
  section?: string;
  /**
   * Groupe de plusieurs écrans (MOKA-248) : l'entrée est active sur chacune de
   * ces routes, pas seulement sur `route`.
   */
  activeRoutes?: string[];
}

/** Ton de la charte associé à chaque module (pastilles, bulles d'icône). */
export type ModuleTone = 'orange' | 'yellow' | 'green' | 'coffee' | 'plum' | 'sky';

const MODULE_TONES: Record<string, ModuleTone> = {
  '/calendar': 'yellow',
  '/room-reservations': 'sky',
  '/project-management': 'orange',
  '/salaries': 'green',
  '/materials': 'coffee',
  '/developpeur': 'plum',
};

@Component({
  selector: 'app-sidenav-v2',
  imports: [SidenavCafeComponent],
  providers: [
    { provide: SIDENAV, useExisting: forwardRef(() => SidenavV2Component) },
    provideIcons({
      iconoirArrowRight,
      iconoirMail,
      iconoirMenu,
      iconoirViewGrid,
      iconoirCalendar,
      iconoirBook,
      iconoirFolder,
      iconoirGraduationCap,
      iconoirCommunity,
      iconoirComputer,
      iconoirLaptop,
      iconoirCode,
      iconoirSettings,
      iconoirCity,
      iconoirSidebarCollapse,
      iconoirSidebarExpand,
      iconoirXmark,
      iconoirCamera,
      iconoirBuilding,
      iconoirLogOut,
      iconoirTransitionRight,
      iconoirNavArrowRight,
      iconoirNavArrowDown,
      iconoirCheck,
      iconoirPlus,
      iconoirMicrophone,
    }),
  ],
  templateUrl: './sidenav-v2.component.html',
  styleUrl: './sidenav-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavV2Component implements OnDestroy {
  @Input() mode: SidebarMode = 'expanded';
  @Output() modeChange = new EventEmitter<SidebarMode>();

  public authService = inject(AuthService);
  private trialBannerService = inject(TrialBannerService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private favoritesService = inject(ProjectFavoritesService);
  private foldersService = inject(ProjectFoldersService);
  private subscriptionFeatures = inject(SubscriptionFeaturesService);
  private voiceOverlay = inject(VoiceOverlayService);
  /** Mode desktop mémorisé (MOKA-349 : ViewPreferencesService, ancienne clé `sidebarMode` relue une fois). */
  private sidebarModePreference = sidebarModePref(inject(ViewPreferencesService));

  readonly assistantEnabled = this.subscriptionFeatures.assistantEnabled;

  /** URL courante : entrée de groupe active sur plusieurs routes (MOKA-248). */
  readonly #url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /**
   * Mobile : le tiroir se referme dès qu'on change d'écran, quel que soit le
   * lien suivi (entrée, sous-entrée, profil, notification…).
   */
  readonly #closeOnNavigation = effect(() => {
    this.#url();
    untracked(() => {
      if (this.isMobile && this.mode !== 'closed') this.setMode('closed');
    });
  });

  /**
   * Mobile : un clic sur un lien du menu referme aussi le tiroir quand il ne
   * change pas d'écran (lien de la page affichée).
   */
  onRailLinkClick(event: Event): void {
    if (!this.isMobile || this.mode === 'closed') return;
    const link = (event.target as HTMLElement | null)?.closest('a[href]');
    if (link) this.setMode('closed');
  }

  /** Vrai si l'entrée (ou une des routes de son groupe) correspond à l'écran affiché. */
  isGroupActive(item: NavItem): boolean {
    const url = this.#url().split(/[?#]/)[0];
    return (item.activeRoutes ?? []).some(r => url === r || url.startsWith(r + '/'));
  }

  /** Sous-entrée « Salles » active (toute la réservation de salles). */
  isRoomsActive = computed(() => {
    const url = this.#url().split(/[?#]/)[0];
    return url === '/room-reservations' || url.startsWith('/room-reservations/');
  });

  public ROLE = Role;
  public isMobile = false;
  private resizeTimeout: ReturnType<typeof setTimeout> | undefined;
  public authServiceProfileSub: Subscription;

  // ─── Rendu café inversé : ton des modules, accès à l'assistant ─────────
  toneFor(route: string): ModuleTone {
    return MODULE_TONES[route] ?? 'coffee';
  }

  openAssistant(): void {
    this.voiceOverlay.open();
    if (this.isMobile) this.setMode('closed');
  }

  // ─── Popover states ──────────────────────────────────────────────────────
  showProfileMenu = signal(false);
  showOrgSwitcher = signal(false);

  public displayDocumentation: boolean =
    (this.authService.hasRoleOrSuperAdmin([this.ROLE.SuperAdmin]) &&
      this.authService.profile?.['email']?.indexOf('@web-atrio.com') !== -1) ||
    window.location.hostname === 'localhost';

  hasMultipleOrgs = computed(() => (this.authService.profile?.organisations?.length ?? 0) > 1);

  private roleLabels: Record<string, string> = {
    [Role.SuperAdmin]: 'Super Admin',
    [Role.ComiteSalaryManager]: 'Gestionnaire RH',
    [Role.InventoryManager]: 'Gestionnaire inventaire',
    [Role.CalendarsManager]: 'Gestionnaire calendrier',
    [Role.WaAdmin]: 'Administrateur Mokah+',
    [Role.Projects]: 'Projets',
  };

  userRoleLabels = computed<string[]>(() => {
    const roles = this.authService.roles;
    if (!roles || roles.length === 0) return [];
    // Filtre aussi une liste mise en cache (localStorage) avant MOKA-408, qui contenait des ''.
    return roles.filter(Boolean).map(r => this.roleLabels[r] ?? r);
  });

  /**
   * Badge d'essai : délégué à TrialBannerService, qui porte le seuil.
   *
   * Il tait le badge quand l'échéance est lointaine, comme le bandeau : un essai
   * de deux ans n'a pas à s'afficher pendant deux ans.
   */
  trialLabel = computed<string | null>(() => this.trialBannerService.trialBadge());

  navItems = computed<NavItem[]>(() => {
    // Pour les utilisateurs externes, seul le module Projets est accessible dans la sidebar
    if (this.authService.isExternalUser) {
      const items: NavItem[] = [];
      if (this.isModuleEnabled('projects_enable')) {
        items.push({
          label: 'Projets',
          route: '/project-management',
          icon: 'iconoirFolder',
          color: 'var(--cyan-500)',
          section: 'apps',
        });
      }
      return items;
    }

    const items: NavItem[] = [];

    // Dashboard
    items.push({
      label: 'Accueil',
      route: '/dashboard',
      icon: 'iconoirViewGrid',
      color: 'var(--blue-500)',
      routerLinkExact: true,
    });

    // Module Calendrier (MOKA-248) : présences (calendriers de l'organisation) et
    // réservation de salles réunies sous une seule entrée, sous-entrées dans le
    // menu (calendriers, puis « Salles »).
    if (this.isModuleEnabled('calendars_enable')) {
      items.push({
        label: 'Espaces & ressources',
        route: '/calendar',
        icon: 'iconoirCalendar',
        color: 'var(--green-500)',
        section: 'apps',
        activeRoutes: ['/calendar', '/room-reservations'],
      });
    }

    // Projects
    if (this.isModuleEnabled('projects_enable')) {
      items.push({
        label: 'Projets',
        route: '/project-management',
        icon: 'iconoirFolder',
        color: 'var(--cyan-500)',
        section: 'apps',
      });
    }

    // RH
    if (
      this.isModuleEnabled('human_resources_enable') &&
      this.authService.hasRoleOrSuperAdmin([this.ROLE.ComiteSalaryManager])
    ) {
      items.push({
        label: 'Ressources humaines',
        route: '/salaries',
        icon: 'iconoirCommunity',
        color: 'var(--orange-500)',
        badge: () => this.authService.negociationInProcess(),
        section: 'apps',
      });
    }

    // Inventaire (réservé aux gestionnaires)
    if (
      this.isModuleEnabled('inventory_enable') &&
      this.authService.hasRoleOrSuperAdmin([this.ROLE.InventoryManager])
    ) {
      items.push({
        label: 'Inventaire',
        route: '/materials',
        icon: 'iconoirComputer',
        color: 'var(--yellow-500)',
        badge:
          this.authService.hasRoleOrSuperAdmin([this.ROLE.InventoryManager])
            ? () => this.authService.materialClaims()
            : undefined,
        section: 'apps',
      });
    }

    // Hub Développeur (MOKA-305) : tout le monde sur un poste local ; sur un domaine
    // deploye (dev, prod), les Administrateurs Mokah+ seulement (meme regle que le garde).
    if (isLocalHost() || isMokahAdmin(this.authService)) {
      items.push({
        label: 'Développeur',
        route: '/developpeur',
        icon: 'iconoirCode',
        color: 'var(--neutral-500)',
        section: 'apps',
      });
    }

    // Admin
    if (this.authService.hasRoleOrSuperAdmin([this.ROLE.SuperAdmin])) {
      items.push({
        label: 'Organisation',
        route: '/admin',
        icon: 'iconoirSettings',
        color: 'var(--red-500)',
        section: 'admin',
      });
    }

    if (this.authService.hasRole([this.ROLE.WaAdmin])) {
      items.push({
        label: 'Administration Mokah+',
        route: '/mokah-admin',
        icon: 'iconoirCity',
        color: 'var(--red-500)',
        section: 'admin',
      });
    }

    return items;
  });

  personalNavItems = computed(() =>
    this.navItems().filter(i => !i.section)
  );

  appNavItems = computed(() =>
    this.navItems().filter(i => i.section === 'apps')
  );

  mainNavItems = computed(() =>
    this.navItems().filter(i => i.section !== 'admin')
  );

  adminNavItems = computed(() =>
    this.navItems().filter(i => i.section === 'admin')
  );

  // ─── Sous-liste rapide "Mes projets" (sous l'item Projets) ────────────────
  // Pas d'appel Supabase dédié : réutilise authService.profile déjà en mémoire
  // (managed_projects / project_memberships), comme project-layout.component.ts.
  #userProjects = signal<UserProject[]>([]);

  quickProjects = computed<UserProject[]>(() => {
    const all = this.#userProjects();
    if (all.length === 0) return [];

    const favoriteIds = this.favoritesService.favoriteIdsSignal();
    const favorites = all.filter(p => favoriteIds.has(String(p.id)));
    const others = all.filter(p => !favoriteIds.has(String(p.id)));

    // Un projet déjà listé sous un dossier favori n'est pas répété plus bas.
    const inFolders = this.projectsInFavoriteFolders();
    return [...favorites, ...others].filter(p => !inFolders.has(String(p.id)));
  });

  /**
   * Sous-menu « Projets » : 5 entrées au plus, dossiers favoris d'abord, puis
   * projets. Un dossier n'affiche pas ses projets : son lien ouvre le hub avec
   * ce dossier sélectionné (?dossier=<id>).
   */
  static readonly PROJECT_MENU_MAX = 5;

  menuFolders = computed(() => this.favoriteFolderGroups().slice(0, SidenavV2Component.PROJECT_MENU_MAX));

  menuProjects = computed(() =>
    this.quickProjects().slice(0, Math.max(0, SidenavV2Component.PROJECT_MENU_MAX - this.menuFolders().length))
  );

  // ─── Dossiers favoris (MOKA-214) : un niveau d'arborescence de plus ────────
  /** Dossiers épinglés par l'utilisateur, avec leurs projets visibles (pour l'exclusion ci-dessous). */
  favoriteFolderGroups = computed(() => {
    const projects = this.foldersService.favoriteFolderProjects();
    return this.foldersService.favoriteFolders().map(folder => ({
      folder,
      projects: projects.get(folder.id) ?? []
    }));
  });

  private projectsInFavoriteFolders = computed(() => {
    const ids = new Set<string>();
    for (const list of this.foldersService.favoriteFolderProjects().values()) {
      for (const p of list) ids.add(p.id);
    }
    return ids;
  });

  /** Sous-liste « Projets » non vide : dossiers favoris ou projets rapides. */
  hasProjectSubitems = computed(() => this.quickProjects().length > 0 || this.favoriteFolderGroups().length > 0);

  getProjectSlug(project: UserProject): string {
    return project.identifier?.toLowerCase() || String(project.id);
  }

  private getUserProjects(): UserProject[] {
    const profile = this.authService.profile as Record<string, unknown> | null;
    if (!profile) return [];

    const result: UserProject[] = [];

    const managed = profile['managed_projects'] as UserProject[] | undefined;
    if (managed) result.push(...managed);

    const memberships = profile['project_memberships'] as ProjectMembership[] | undefined;
    if (memberships) {
      for (const m of memberships) {
        if (m.project && !result.some(p => p.id === m.project!.id)) {
          result.push(m.project as UserProject);
        }
      }
    }

    return result.sort((a, b) => a.name.localeCompare(b.name));
  }

  private refreshProjects(): void {
    this.#userProjects.set(this.getUserProjects());
  }

  // ─── Toutes les projets (pour l'action rapide "Nouvelle tâche" génériques) ──
  allProjects = computed<UserProject[]>(() => this.#userProjects());

  showProjectsParentPlus = computed<boolean>(() => {
    const count = this.allProjects().length;
    return count === 1 || count > 4;
  });

  showProjectsSubitemPlus = computed<boolean>(() => this.allProjects().length >= 2);

  hasCalendars = computed<boolean>(
    () => (this.authService.profile?.organisation?.calendars?.length ?? 0) > 0
  );

  /**
   * Sous-menu « Espaces » : les calendriers de l'organisation (5 au plus), quand
   * il y en a plusieurs. Même grammaire que les projets sous « Projets ».
   * `profileRevision` : `profile` est un getter, invisible des signaux.
   */
  static readonly CALENDAR_MENU_MAX = 5;

  menuCalendars = computed<{ id: number; label: string; slug: string }[]>(() => {
    this.authService.profileRevision();
    const calendars = (this.authService.profile?.organisation?.calendars ?? []) as { id: number; label: string; slug: string }[];
    return calendars.length > 1
      ? calendars.filter(c => !!c.slug).slice(0, SidenavV2Component.CALENDAR_MENU_MAX)
      : [];
  });

  /**
   * Sous-entrées « Espaces & ressources » (MOKA-248) : TOUS les calendriers de
   * l'organisation (5 au plus, même un seul : son nom dit ce qu'il contient),
   * puis « Salles ».
   */
  spaceCalendars = computed<{ id: number; label: string; slug: string }[]>(() => {
    this.authService.profileRevision();
    const calendars = (this.authService.profile?.organisation?.calendars ?? []) as { id: number; label: string; slug: string }[];
    return calendars.filter(c => !!c.slug).slice(0, SidenavV2Component.CALENDAR_MENU_MAX);
  });

  /** L'entrée porte des sous-entrées (calendriers et salles). */
  isSpacesGroup(item: NavItem): boolean {
    return item.route === '/calendar';
  }

  // ─── Flyout sous-items projets (mode collapsed, desktop uniquement) ────────
  openFlyoutRoute = signal<string | null>(null);
  private flyoutCloseTimeout: ReturnType<typeof setTimeout> | undefined;

  itemHasFlyout(item: NavItem): boolean {
    if (this.isMobile) return false;
    return (item.route === '/project-management' && this.hasProjectSubitems()) || this.isSpacesGroup(item);
  }

  isFlyoutOpen(route: string): boolean {
    return this.openFlyoutRoute() === route;
  }

  onFlyoutAnchorMouseEnter(item: NavItem): void {
    if (this.mode === 'collapsed' && this.itemHasFlyout(item)) {
      clearTimeout(this.flyoutCloseTimeout);
      this.openFlyoutRoute.set(item.route);
    }
  }

  onFlyoutAnchorMouseLeave(item: NavItem): void {
    if (this.itemHasFlyout(item)) {
      this.scheduleCloseFlyout();
    }
  }

  onFlyoutPanelMouseEnter(): void {
    clearTimeout(this.flyoutCloseTimeout);
  }

  onFlyoutPanelMouseLeave(): void {
    this.scheduleCloseFlyout();
  }

  private scheduleCloseFlyout(): void {
    clearTimeout(this.flyoutCloseTimeout);
    this.flyoutCloseTimeout = setTimeout(() => this.openFlyoutRoute.set(null), 150);
  }

  closeFlyout(): void {
    clearTimeout(this.flyoutCloseTimeout);
    this.openFlyoutRoute.set(null);
  }

  onNavItemClick(event: Event, item: NavItem): void {
    // En collapsed, l'item Projets ouvre/ferme le flyout au click plutôt que de
    // naviguer directement : le flyout offre déjà l'accès aux projets et à
    // "Voir les autres" (qui navigue, lui). Nécessaire pour clavier/touch,
    // le survol seul n'étant pas accessible à ces modes d'interaction.
    if (this.mode === 'collapsed' && this.itemHasFlyout(item)) {
      event.preventDefault();
      clearTimeout(this.flyoutCloseTimeout);
      this.openFlyoutRoute.update(current => (current === item.route ? null : item.route));
    }
  }

  // ─── Actions rapides [+] ─────────────────────────────────────────────────
  openQuickActionKey = signal<string | null>(null);
  quickTaskProjects = signal<UserProject[]>([]);
  quickTaskInitialProjectId = signal<string | null>(null);

  isQuickActionOpen(key: string): boolean {
    return this.openQuickActionKey() === key;
  }

  openQuickPresence(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeFlyout();
    this.openQuickActionKey.set('presence');
  }

  openQuickInventory(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeFlyout();
    this.openQuickActionKey.set('inventory');
  }

  openQuickTaskForProject(event: Event, project: UserProject): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeFlyout();
    this.quickTaskProjects.set([project]);
    this.quickTaskInitialProjectId.set(project.id);
    this.openQuickActionKey.set(`task-${project.id}`);
  }

  openQuickTaskGeneric(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeFlyout();
    const all = this.allProjects();
    this.quickTaskProjects.set(all);
    this.quickTaskInitialProjectId.set(all[0]?.id ?? null);
    this.openQuickActionKey.set('task-parent');
  }

  closeQuickAction(): void {
    this.openQuickActionKey.set(null);
  }

  onQuickActionCreated(): void {
    this.closeQuickAction();
    if (this.isMobile) {
      this.setMode('closed');
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeFlyout();
    this.closeQuickAction();
  }

  isModuleEnabled(moduleKey: ModuleKey): boolean {
    if (this.authService.profile?.external) {
      const accessKey = this.resolveExternalRoleKey(moduleKey);
      if (accessKey) {
        return this.authService.profile.roles_externals.some(
          (role: ExternalRole) => role.role.identifier === accessKey
        );
      }
      return this.authService.profile.roles_externals.some(
        (role: ExternalRole) => role.role.identifier === moduleKey
      );
    }

    // Source unique de vérité : l'abonnement (fallback TRUE sans abonnement,
    // aligné sur isModuleEnabledGuard).
    return this.subscriptionFeatures.moduleEnabled(moduleKey);
  }

  private resolveExternalRoleKey(moduleKey: ModuleKey): Role | null {
    switch (moduleKey) {
      case 'calendars_enable':
        return Role.CalendarsManager;
      case 'projects_enable':
        return Role.Projects;
      case 'human_resources_enable':
        return Role.ComiteSalaryManager;
      case 'inventory_enable':
        return Role.InventoryManager;
      default:
        return null;
    }
  }

  // ─── Popover profile ─────────────────────────────────────────────────────

  toggleProfileMenu(): void {
    this.showProfileMenu.update(v => !v);
    this.showOrgSwitcher.set(false);
  }

  closeProfileMenu(): void {
    this.showProfileMenu.set(false);
    this.showOrgSwitcher.set(false);
  }

  async onLogout(): Promise<void> {
    await this.authService.signOut();
    this.closeProfileMenu();
  }

  // ─── Selecteur d'organisation ────────────────────────────────────────────

  toggleOrgSwitcher(): void {
    this.showOrgSwitcher.update(v => !v);
    this.showProfileMenu.set(false);
  }

  closeOrgSwitcher(): void {
    this.showOrgSwitcher.set(false);
  }

  goToAccount(): void {
    this.router.navigate(['/profile/account']);
    this.closeProfileMenu();
  }

  /** Profil > Notifications : les e-mails reçus, par module (MOKA-255). */
  goToEmailPreferences(): void {
    this.router.navigate(['/profile/notifications']);
    this.closeProfileMenu();
  }

  goToCareer(): void {
    this.router.navigate(['/profile/career']);
    this.closeProfileMenu();
  }

  goToMyDevices(): void {
    this.router.navigate(['/materials/my-devices']);
    this.closeProfileMenu();
  }

  /**
   * Toutes mes appartenances encore valides, organisation active INCLUSE et
   * triées par libellé.
   *
   * Le sélecteur les liste toutes, celle où l'on se trouve comprise, marquée
   * comme actuelle : c'est ce qui permet de savoir où l'on est sans deviner.
   *
   * `profileRevision` est la dépendance réactive — `profile` est un getter, donc
   * invisible du système de réactivité d'Angular.
   */
  myOrganisations = computed(() => {
    this.authService.profileRevision();
    return (this.authService.profile?.organisations ?? [])
      .filter(membership => membership.is_active && !membership.left_at)
      .sort((a, b) =>
        (a.organisation?.label ?? '').localeCompare(b.organisation?.label ?? '')
      );
  });

  /** Libellé de l'organisation active, affiché sous l'identité. */
  activeOrganisationLabel = computed(() => {
    this.authService.profileRevision();
    return this.authService.profile?.['organisation']?.label ?? null;
  });

  isActiveOrganisation(orgId: number): boolean {
    return orgId === this.authService.getOrganisationId();
  }

  async switchOrganisation(orgId: number): Promise<void> {
    if (this.isActiveOrganisation(orgId)) {
      this.closeOrgSwitcher();
      return;
    }
    this.closeOrgSwitcher();
    // Implémentation unique dans AuthService : elle passe par l'edge function
    // switch-organisation, purge le cache local et recharge l'application.
    await this.authService.switchOrganisation(orgId);
  }

  /**
   * Crée une organisation dont on devient administrateur.
   *
   * Le parcours est celui de l'inscription : l'edge function
   * `create-new-organisation` monte l'organisation, l'appartenance, le rôle
   * Admin, le calendrier, l'agence, la grille et l'abonnement d'essai — puis on
   * bascule dessus. La seule différence avec une inscription est que le compte
   * existe déjà.
   */
  createOrganisation(): void {
    this.closeOrgSwitcher();
    this.authService.createNewOrganisationAndSwitchToIt();
  }


  openAvatarChangeDialog(): void {
    const profile = this.authService.profile;
    const dialogData: AvatarChangeDialogData = {
      currentPhotoUrl: profile?.['photo_url'] || undefined,
      firstName: profile?.['first_name'] || '',
      lastName: profile?.['last_name'] || '',
    };

    const dialogRef = this.dialog.open(AvatarChangeDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: { photoUrl: string | null } | undefined) => {
        // '' = avatar à initiales (photo vidée) : à enregistrer aussi.
        if (result && result.photoUrl !== null && result.photoUrl !== undefined) {
          this.authService.updateProfile({
            key: 'photo_url',
            value: result.photoUrl,
          });
        }
      });
  }

  // ─── Mode & device ───────────────────────────────────────────────────────

  constructor() {
    // isMobile est fixé AVANT toute logique de transition : l'ancien
    // checkDevice() au constructeur voyait un faux passage desktop→mobile
    // (isMobile initialisé à false) et persistait 'closed', écrasant la
    // préférence desktop de l'utilisateur à chaque chargement en largeur mobile.
    this.isMobile = window.innerWidth < 768;
    window.addEventListener('resize', this.onWindowResize);

    // 'closed' est un état de session mobile, jamais une préférence : le
    // localStorage ne contient que 'expanded'/'collapsed' (voir setMode).
    const savedMode = this.readSavedDesktopMode();
    if (this.isMobile) {
      this.mode = 'closed';
    } else {
      this.mode = savedMode ?? (window.innerWidth > 1200 ? 'expanded' : 'collapsed');
    }

    this.refreshProjects();
    this.favoritesService.loadFavorites();
    if (this.isModuleEnabled('projects_enable')) void this.foldersService.load();

    effect(() => {
      if (this.authService.projectsListHasChangeAndUpdated()) {
        this.refreshProjects();
        void this.foldersService.loadFavoriteFolderProjects();
      }
    });

    this.authServiceProfileSub =
      this.authService.profileHasChange.subscribe(() => {
        this.refreshProjects();
        // Changement d'organisation active : les dossiers ne sont plus les mêmes.
        this.foldersService.reset();
        if (this.isModuleEnabled('projects_enable')) void this.foldersService.load(true);
      });
  }

  private onWindowResize = (): void => {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.checkDevice(), 150);
  };

  private readSavedDesktopMode(): 'expanded' | 'collapsed' | null {
    return this.sidebarModePreference();
  }

  private checkDevice(): void {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;

    if (!wasMobile && this.isMobile) {
      this.setMode('closed');
    } else if (wasMobile && !this.isMobile) {
      if (this.mode === 'closed') {
        // Retour desktop : on restaure la préférence sauvegardée plutôt que
        // de la recalculer depuis la largeur
        this.setMode(this.readSavedDesktopMode() ?? (window.innerWidth > 1200 ? 'expanded' : 'collapsed'));
      }
    }
  }

  setMode(mode: SidebarMode): void {
    if (!this.isMobile && mode === 'closed') {
      mode = 'collapsed';
    }
    this.mode = mode;
    this.modeChange.emit(mode);
    // Seule la préférence desktop est persistée : sur mobile, ouvrir/fermer
    // le drawer est un état de session qui ne doit pas écraser ce choix
    if (!this.isMobile && mode !== 'closed') {
      this.sidebarModePreference.set(mode);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.flyoutCloseTimeout);
    clearTimeout(this.resizeTimeout);
    window.removeEventListener('resize', this.onWindowResize);
    this.authServiceProfileSub.unsubscribe();
  }
}
